import React, { FC, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertBubbleIcon } from '@shopify/polaris-icons';
import { Navigation } from '@shopify/polaris';

import Grid from '@mui/material/Grid';

import { AuthenticationContext } from '../../../homeModules/auth/contexts/AuthenticationContext';
import {
  useBackflowJobGroups,
  useBackflowApprovalQueue,
  useUnscheduledBackflowJobGroups,
} from '../../backflowJobLists/hooks/useBackflowJobGroups';

const getUnsubmittedLocalCount = (): number =>
  Object.keys(localStorage).filter((key) => key.startsWith('backflowJobGroup_')).length;

const BackflowSidebar: FC = () => {
  const location = useLocation();

  const { user } = useContext(AuthenticationContext);

  const { data: backflowJobGroupsData } = useBackflowJobGroups();
  const backflowTestsBadge = backflowJobGroupsData?.data?.result?.length;
  const backflowTestsBadgeDisplay = backflowTestsBadge > 0 ? String(backflowTestsBadge) : undefined;

  const { data: approvalQueueData } = useBackflowApprovalQueue();
  const approvalQueueBadge = approvalQueueData?.data?.result?.length;
  const approvalQueueBadgeDisplay = approvalQueueBadge > 0 ? String(approvalQueueBadge) : undefined;

  const { data: unscheduledData } = useUnscheduledBackflowJobGroups();
  const unscheduledBadge = unscheduledData?.data?.result?.length;
  const unscheduledBadgeDisplay = unscheduledBadge > 0 ? String(unscheduledBadge) : undefined;

  const unsubmittedCount = getUnsubmittedLocalCount();
  const unsubmittedBadgeDisplay = unsubmittedCount > 0 ? String(unsubmittedCount) : undefined;

  const isApprover = user?.modules?.some((module) => module.value === 'backflow_testing' && module.can_review);
  const isAdminUser = user?.modules?.some((module) => module.value === 'backflow_testing' && parseInt(module.tier) > 2);

  const hasAdminAccess = isAdminUser || isApprover;
  const navigationItems = [];

  if (hasAdminAccess) {
    navigationItems.push({
      label: 'Queues',
      url: '/backflow_tests/search',
      icon: AlertBubbleIcon as any,
      matches: true,
      subNavigationItems: [
        {
          url: '/backflow_tests/search',
          disabled: false,
          label: 'Backflow Tests',
          badge: backflowTestsBadgeDisplay,
        },
        {
          url: '/backflow_tests/unscheduled_tests',
          disabled: false,
          label: 'Unscheduled Tests',
          badge: unscheduledBadgeDisplay,
        },
        {
          url: '/backflow_tests/unsubmitted',
          disabled: false,
          label: 'Your Unsubmitted Tests',
          badge: unsubmittedBadgeDisplay,
        },
        {
          url: '/backflow_tests/approvals',
          disabled: false,
          label: 'Approval Queue',
          badge: approvalQueueBadgeDisplay,
        },
        {
          url: '/backflow_tests/water_authority_submissions',
          disabled: false,
          label: 'Water Authority Tracking',
        },
        {
          url: '/backflow_tests/flagged_devices',
          disabled: false,
          label: 'Flagged Devices',
        },
      ],
    });
    navigationItems.push({
      label: 'Module Settings',
      url: '/backflow_tests/water_authority_configurations',
      icon: AlertBubbleIcon as any,
      matches: true,
      subNavigationItems: [
        {
          url: '/backflow_tests/water_authority_configurations',
          disabled: false,
          label: 'Water Authority Config',
        },
        {
          url: '/backflow_tests/plumber_config',
          disabled: false,
          label: 'Plumber Config',
        },
        {
          url: '/backflow_tests/servman_sql_logs',
          disabled: false,
          label: 'Servman Logs',
        },
      ],
    });
  } else if (isApprover) {
    navigationItems.push({
      label: 'Backflow Testing',
      url: '/backflow_tests/search',
      icon: AlertBubbleIcon as any,
      matches: true,
      subNavigationItems: [
        {
          url: '/backflow_tests/search',
          disabled: false,
          label: 'Scheduled Backflow Tests',
        },
        {
          url: '/backflow_tests/search',
          disabled: false,
          label: 'Unscheduled Backflow Tests',
        },
        {
          url: '/backflow_tests/unsubmitted',
          disabled: false,
          label: 'Your Unsubmitted Tests',
          badge: unsubmittedBadgeDisplay,
        },
        {
          url: '/backflow_tests/approvals',
          disabled: false,
          label: 'Approval Queue',
        },
      ],
    });
  } else {
    navigationItems.push({
      label: 'Backflow Testing',
      url: '/backflow_tests/search',
      icon: AlertBubbleIcon as any,
      matches: true,
      subNavigationItems: [
        {
          url: '/backflow_tests/search',
          disabled: false,
          label: 'Backflow Tests',
        },
        {
          url: '/backflow_tests/unsubmitted',
          disabled: false,
          label: 'Your Unsubmitted Tests',
          badge: unsubmittedBadgeDisplay,
        },
      ],
    });
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <Navigation location={location.pathname}>
          <Navigation.Section items={navigationItems} />
        </Navigation>
      </Grid>
    </Grid>
  );
};

export default BackflowSidebar;
